// ABOUTME: Section editor component with TipTap rich text editing for all sections.
// ABOUTME: Handles text selection for Magic Edit and section content updates.

import { useRef, useCallback, useState, useEffect, forwardRef, useImperativeHandle } from 'react';
import { motion } from 'framer-motion';
import { GripVertical, Trash2, MoreHorizontal, Type, Heading1 } from 'lucide-react';
import type { ReportSection } from '@/types/api';
import { RichTextEditor, type RichTextEditorRef, type SelectionState } from '@/components/ui/RichTextEditor';
import type { Editor } from '@tiptap/react';

interface TextSelection {
  sectionId: string;
  startOffset: number;
  endOffset: number;
  selectedText: string;
  rect?: DOMRect;
}

export interface SectionEditorRef {
  getEditor: () => Editor | null;
  focus: () => void;
}

interface SectionEditorProps {
  section: ReportSection;
  onUpdate: (content: string) => void;
  onDelete: () => void;
  onSelect: (selection: TextSelection | null) => void;
  onTypeChange: (type: ReportSection['type']) => void;
  onFocusChange?: (focused: boolean, sectionId: string) => void;
  isHighlighted: boolean;
  highlightRange?: { start: number; end: number };
  isDragging?: boolean;
  dragHandleProps?: Record<string, unknown>;
}

export const SectionEditor = forwardRef<SectionEditorRef, SectionEditorProps>(({
  section,
  onUpdate,
  onDelete,
  onSelect,
  onTypeChange,
  onFocusChange,
  isHighlighted,
  highlightRange,
  isDragging,
  dragHandleProps,
}, ref) => {
  const richTextRef = useRef<RichTextEditorRef>(null);
  const [showMenu, setShowMenu] = useState(false);
  const [isFocused, setIsFocused] = useState(false);

  // Expose editor methods to parent
  useImperativeHandle(ref, () => ({
    getEditor: () => richTextRef.current?.getEditor() ?? null,
    focus: () => richTextRef.current?.getEditor()?.commands.focus(),
  }));

  // Track previous highlight values to avoid infinite re-render loops
  // (highlightRange is a new object reference on every parent render)
  const prevHighlightRef = useRef<{ start: number; end: number } | null>(null);

  // Track previous section type to detect type changes and transform content
  const prevTypeRef = useRef<ReportSection['type']>(section.type);

  // Handle section type changes by transforming content via TipTap commands
  // When user clicks "Heading" or "Paragraph" in the menu, we need to actually
  // transform the HTML content, not just update CSS styling
  useEffect(() => {
    const editor = richTextRef.current?.getEditor();
    if (!editor) return;

    const prevType = prevTypeRef.current;
    const newType = section.type;

    // Only transform if type actually changed
    if (prevType !== newType) {
      prevTypeRef.current = newType;

      // Text -> Heading: Convert all paragraphs to h1
      if (prevType === 'text' && newType === 'heading') {
        editor.chain().focus().selectAll().setHeading({ level: 1 }).run();
        // Emit the updated HTML content so parent state stays in sync
        const newHtml = editor.getHTML();
        onUpdate(newHtml);
      }
      // Heading -> Text: Convert all headings to paragraphs
      else if (prevType === 'heading' && newType === 'text') {
        editor.chain().focus().selectAll().setParagraph().run();
        // Emit the updated HTML content so parent state stays in sync
        const newHtml = editor.getHTML();
        onUpdate(newHtml);
      }
    }
  }, [section.type, onUpdate]);

  // Apply highlight via TipTap decorations - compare by VALUE not reference
  useEffect(() => {
    const newValue = isHighlighted && highlightRange ? highlightRange : null;
    const prevValue = prevHighlightRef.current;

    // Check if values actually changed (not just object reference)
    const valuesChanged =
      (newValue === null) !== (prevValue === null) ||
      (newValue !== null &&
        prevValue !== null &&
        (newValue.start !== prevValue.start || newValue.end !== prevValue.end));

    if (valuesChanged && richTextRef.current) {
      richTextRef.current.setHighlight(newValue);
      prevHighlightRef.current = newValue ? { start: newValue.start, end: newValue.end } : null;
    }
  }, [isHighlighted, highlightRange]);

  // Handle TipTap content changes
  const handleRichTextChange = useCallback(
    (html: string) => {
      onUpdate(html);
    },
    [onUpdate]
  );

  // Handle TipTap selection changes for Magic Edit
  const handleRichTextSelection = useCallback(
    (selection: SelectionState) => {
      if (selection.isEmpty || !selection.selectedText.trim()) {
        onSelect(null);
        return;
      }

      onSelect({
        sectionId: section.id,
        startOffset: selection.startOffset,
        endOffset: selection.endOffset,
        selectedText: selection.selectedText,
      });
    },
    [section.id, onSelect]
  );

  // Handle focus changes
  const handleFocus = useCallback(() => {
    setIsFocused(true);
    onFocusChange?.(true, section.id);
  }, [onFocusChange, section.id]);

  const handleBlur = useCallback(() => {
    setIsFocused(false);
    onFocusChange?.(false, section.id);
  }, [onFocusChange, section.id]);

  // Get styling based on section type
  const getTypeStyles = () => {
    const baseStyles = 'prose-headings:text-surface-800 prose-p:text-surface-600 prose-strong:text-surface-800 prose-li:text-surface-600 prose-code:text-primary-600 prose-code:bg-surface-100 prose-code:px-1 prose-code:rounded-sm';
    switch (section.type) {
      case 'heading':
        return `${baseStyles} prose-h1:text-3xl prose-h1:font-semibold prose-h2:text-2xl prose-h2:font-semibold`;
      case 'text':
        return `${baseStyles} prose-p:leading-relaxed prose-p:text-base prose-li:leading-relaxed`;
      case 'visualization':
        return 'text-sm text-surface-500 italic';
      case 'table':
        return 'text-sm font-mono text-surface-600';
      default:
        return baseStyles;
    }
  };

  return (
    <motion.div
      layout
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
      exit={{ opacity: 0, y: -10 }}
      className={`group relative ${isDragging ? 'opacity-50' : ''}`}
      data-section-id={section.id}
    >
      {/* Section Controls */}
      <div className="absolute -left-12 top-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        {/* Drag Handle */}
        <button
          {...dragHandleProps}
          className="p-1 text-surface-400 hover:text-surface-600 cursor-grab active:cursor-grabbing"
        >
          <GripVertical className="w-4 h-4" />
        </button>
      </div>

      {/* More Options Menu */}
      <div className="absolute -right-10 top-1 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
        <div className="relative">
          <button
            onClick={() => setShowMenu(!showMenu)}
            className="p-1 text-surface-400 hover:text-surface-600 cursor-pointer"
          >
            <MoreHorizontal className="w-4 h-4" />
          </button>

          {showMenu && (
            <div className="absolute right-0 top-full mt-1 w-40 bg-surface-0 border border-surface-200 shadow-dropdown rounded-sm py-1 z-50">
              <button
                onClick={() => {
                  onTypeChange('heading');
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-600 hover:text-surface-800 hover:bg-surface-50 cursor-pointer"
              >
                <Heading1 className="w-4 h-4" />
                Heading
              </button>
              <button
                onClick={() => {
                  onTypeChange('text');
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-surface-600 hover:text-surface-800 hover:bg-surface-50 cursor-pointer"
              >
                <Type className="w-4 h-4" />
                Paragraph
              </button>
              <div className="h-px bg-surface-200 my-1" />
              <button
                onClick={() => {
                  onDelete();
                  setShowMenu(false);
                }}
                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-error hover:bg-error-light cursor-pointer"
              >
                <Trash2 className="w-4 h-4" />
                Delete
              </button>
            </div>
          )}
        </div>
      </div>

      {/* Editor Content - no toolbar, toolbar is at page level */}
      <div className={`rounded-sm transition-all ${isFocused ? 'ring-1 ring-primary-600/25 bg-surface-50' : ''}`}>
        <RichTextEditor
          ref={richTextRef}
          content={section.content || ''}
          onChange={handleRichTextChange}
          onSelectionChange={handleRichTextSelection}
          onFocus={handleFocus}
          onBlur={handleBlur}
          showToolbar={false}
          className={`outline-none min-h-[1.5em] py-2 px-1 prose max-w-none ${getTypeStyles()}`}
        />
      </div>
    </motion.div>
  );
});

SectionEditor.displayName = 'SectionEditor';
